import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography
} from '@mui/material';
import CustomDatePicker from '../../components/CustomDatePicker';
import CustomDropdown from '../../components/CustomDropdown';
import CustomTextArea from '../../components/CustomTextArea';
import CustomTextInput from '../../components/CustomTextInput';
import CursorTooltip from '../../components/CursorTooltip';
import FrequencySentenceDisplay from '../../components/FrequencySentenceDisplay';
import apiService from '../../services/api';
import { formatGridDate, parseDateOnly, toIsoDateString } from '../../utils/dateUtils';
import { getMaxGenerateDaysBefore } from '../../utils/frequencyCalculations';
import { normalizeDashboardTableNameForCalendar } from '../../utils/tableNameNormalize';
import {
  TODO_COLOR_PALETTE,
  TODO_FREQUENCY_OPTIONS,
  TODO_PRIORITIES,
  calculateTodoNextDueDate,
  calculateTodoNextRun,
  emptyTodoFrequency,
  formatAssignedRecordOptionLabel,
  formatLockedLinkedRecordLabel,
  hasRequiredTodoFrequencyOptions,
  normalizeTodoColor,
  todoFrequencyFromRecord,
  uniquifyAssignedRecordOptionLabels
} from './todoShared';

const YesNoRadio = ({ value, onChange, label, disabled }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
    <span style={{ color: 'var(--color-text)', fontWeight: 500, marginRight: 8 }}>{label}</span>
    <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: disabled ? 'not-allowed' : 'pointer', color: 'var(--color-text)' }}>
      <input
        type="radio"
        checked={value === true}
        onChange={() => onChange(true)}
        disabled={disabled}
        style={{ marginRight: 4 }}
      />
      Yes
    </label>
    <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: disabled ? 'not-allowed' : 'pointer', color: 'var(--color-text)' }}>
      <input
        type="radio"
        checked={value === false}
        onChange={() => onChange(false)}
        disabled={disabled}
        style={{ marginRight: 4 }}
      />
      No
    </label>
  </div>
);

const emptyForm = () => ({
  Title: '',
  Notes: '',
  DueDate: null,
  Priority: 'Medium',
  Completed: false,
  LinkedTableName: '',
  LinkedRecordId: '',
  Color: '',
  Label: '',
  ...emptyTodoFrequency()
});

const TodoFormModal = ({
  open,
  onClose,
  todo = null,
  viewMode = false,
  onSaved,
  onOpenLinkedRecord,
  presetLink = null
}) => {
  const [formData, setFormData] = useState(emptyForm());
  const [assignedRecords, setAssignedRecords] = useState([]);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [assignedError, setAssignedError] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');
  const [titleError, setTitleError] = useState('');
  const [dueDateError, setDueDateError] = useState('');
  const [frequencyError, setFrequencyError] = useState('');
  const lockLink = Boolean(presetLink?.tableName && presetLink?.recordId != null && String(presetLink.recordId) !== '');
  const fieldsDisabled = Boolean(viewMode) || loading;
  const nextAlreadyCreated = Boolean(todo?.NextRecordGenerated);

  useEffect(() => {
    if (!open) return;
    setError('');
    setConfirmDelete(false);
    setTitleError('');
    setDueDateError('');
    setFrequencyError('');
    if (todo) {
      setFormData({
        Title: todo.Title || '',
        Notes: todo.Notes || '',
        DueDate: parseDateOnly(todo.DueDate),
        Priority: todo.Priority || 'Medium',
        Completed: Boolean(todo.Completed),
        LinkedTableName: normalizeDashboardTableNameForCalendar(todo.LinkedTableName) || todo.LinkedTableName || '',
        LinkedRecordId: todo.LinkedRecordId != null ? String(todo.LinkedRecordId) : '',
        Color: normalizeTodoColor(todo.Color),
        Label: todo.Label || '',
        ...todoFrequencyFromRecord(todo)
      });
    } else {
      const next = emptyForm();
      if (lockLink) {
        next.LinkedTableName = normalizeDashboardTableNameForCalendar(presetLink.tableName) || presetLink.tableName;
        next.LinkedRecordId = String(presetLink.recordId);
        if (presetLink.dueDate) next.DueDate = parseDateOnly(presetLink.dueDate);
      }
      setFormData(next);
    }
  }, [open, todo, lockLink, presetLink]);

  useEffect(() => {
    if (!open || lockLink) {
      setAssignedRecords([]);
      setAssignedLoading(false);
      setAssignedError('');
      return undefined;
    }
    let cancelled = false;
    setAssignedLoading(true);
    setAssignedError('');
    (async () => {
      try {
        const response = await apiService.getTodoAssignedRecords();
        if (cancelled) return;
        const list = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
        setAssignedRecords(list);
      } catch (err) {
        if (cancelled) return;
        setAssignedRecords([]);
        setAssignedError(err.message || 'Could not load assigned records.');
      } finally {
        if (!cancelled) setAssignedLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, lockLink]);

  const formTypeOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    assignedRecords.forEach((row) => {
      const tableName = normalizeDashboardTableNameForCalendar(row.tableName) || String(row.tableName || '').trim();
      if (!tableName || seen.has(tableName)) return;
      seen.add(tableName);
      opts.push({
        value: tableName,
        label: row.label || tableName
      });
    });
    const existingTable = normalizeDashboardTableNameForCalendar(todo?.LinkedTableName) || todo?.LinkedTableName;
    if (existingTable && !seen.has(existingTable)) {
      opts.push({
        value: existingTable,
        label: existingTable
      });
    }
    opts.sort((a, b) => a.label.localeCompare(b.label));
    return opts;
  }, [assignedRecords, todo]);

  const recordOptions = useMemo(() => {
    const tableName = formData.LinkedTableName;
    if (!tableName) return [];
    const selectedType = normalizeDashboardTableNameForCalendar(tableName) || tableName;
    const seen = new Set();
    const opts = [];
    assignedRecords.forEach((row) => {
      const rowType = normalizeDashboardTableNameForCalendar(row.tableName) || String(row.tableName || '');
      if (rowType !== selectedType) return;
      const recordId = String(row.recordId ?? '');
      if (!recordId || seen.has(recordId)) return;
      seen.add(recordId);
      opts.push({
        value: recordId,
        label: formatAssignedRecordOptionLabel(row) || `#${recordId}`
      });
    });
    const existingType = normalizeDashboardTableNameForCalendar(todo?.LinkedTableName) || todo?.LinkedTableName;
    if (
      existingType === selectedType &&
      todo.LinkedRecordId != null &&
      todo.LinkedRecordId !== '' &&
      !seen.has(String(todo.LinkedRecordId))
    ) {
      opts.push({
        value: String(todo.LinkedRecordId),
        label: `#${todo.LinkedRecordId}`
      });
    }
    opts.sort((a, b) => a.label.localeCompare(b.label));
    return uniquifyAssignedRecordOptionLabels(opts);
  }, [assignedRecords, formData.LinkedTableName, todo]);

  const linkedInfo = {
    tableName: formData.LinkedTableName || null,
    recordId: formData.LinkedRecordId || null
  };
  const canOpenLinked = Boolean(linkedInfo.tableName && linkedInfo.recordId && onOpenLinkedRecord);

  const frequencyConfig = {
    FrequencyDayOfWeek: formData.FrequencyDayOfWeek,
    FrequencyWeekType: formData.FrequencyWeekType,
    FrequencyDayType: formData.FrequencyDayType,
    FrequencyDayNumber: formData.FrequencyDayNumber,
    FrequencyWeekday: formData.FrequencyWeekday,
    FrequencyMonthType: formData.FrequencyMonthType,
    FrequencyMonth: formData.FrequencyMonth,
    FrequencyYears: formData.FrequencyYears,
    GenerateDaysBefore: formData.GenerateDaysBefore
  };

  const nextDueDate = useMemo(() => {
    if (!formData.Repeating || !formData.DueDate || !formData.Frequency) return null;
    return calculateTodoNextDueDate(formData.DueDate, formData.Frequency, formData);
  }, [formData]);

  const nextRunDate = useMemo(() => {
    if (!formData.Repeating || !formData.DueDate || !formData.Frequency) return null;
    return calculateTodoNextRun(formData.DueDate, formData.Frequency, formData.GenerateDaysBefore, formData);
  }, [formData]);

  const handleFormTypeChange = (value) => {
    const tableName = value || '';
    setFormData((prev) => ({
      ...prev,
      LinkedTableName: tableName,
      LinkedRecordId: tableName === prev.LinkedTableName ? prev.LinkedRecordId : ''
    }));
  };

  const handleRepeatingChange = (checked) => {
    setDueDateError('');
    setFrequencyError('');
    setFormData((prev) => ({
      ...prev,
      ...(checked ? { Repeating: true } : emptyTodoFrequency()),
      Repeating: checked
    }));
  };

  const handleFrequencyChange = (value) => {
    const frequency = value || '';
    const maxDays = getMaxGenerateDaysBefore(frequency, formData.FrequencyYears);
    const validMax = maxDays >= 0 ? maxDays : 0;
    const currentDays = formData.GenerateDaysBefore || 0;
    setFrequencyError('');
    setFormData((prev) => ({
      ...prev,
      Frequency: frequency,
      GenerateDaysBefore: currentDays > validMax ? validMax : prev.GenerateDaysBefore
    }));
  };

  const handleSave = async () => {
    if (viewMode) return;
    const title = (formData.Title || '').trim();
    if (!title) {
      setTitleError('Title is required');
      return;
    }
    setTitleError('');
    if (formData.Repeating) {
      if (!formData.DueDate) {
        setDueDateError('Due date is required for repeating to-dos');
        return;
      }
      if (!formData.Frequency) {
        setFrequencyError('Frequency is required for repeating to-dos');
        return;
      }
      if (!hasRequiredTodoFrequencyOptions(formData.Frequency, formData)) {
        setFrequencyError('Complete the frequency schedule');
        return;
      }
    }
    setDueDateError('');
    setFrequencyError('');
    setLoading(true);
    setError('');
    try {
      const payload = {
        Title: title,
        Notes: formData.Notes || null,
        DueDate: toIsoDateString(formData.DueDate),
        Priority: formData.Priority || 'Medium',
        Completed: Boolean(formData.Completed),
        LinkedTableName: linkedInfo.tableName && linkedInfo.recordId ? linkedInfo.tableName : null,
        LinkedRecordId: linkedInfo.tableName && linkedInfo.recordId ? linkedInfo.recordId : null,
        Color: normalizeTodoColor(formData.Color) || null,
        Label: (formData.Label || '').trim() || null,
        Repeating: Boolean(formData.Repeating),
        Frequency: formData.Repeating ? (formData.Frequency || null) : null,
        GenerateDaysBefore: formData.Repeating ? (formData.GenerateDaysBefore || 0) : null,
        FrequencyDayOfWeek: formData.Repeating ? formData.FrequencyDayOfWeek : null,
        FrequencyWeekType: formData.Repeating ? formData.FrequencyWeekType : null,
        FrequencyDayType: formData.Repeating ? formData.FrequencyDayType : null,
        FrequencyDayNumber: formData.Repeating ? formData.FrequencyDayNumber : null,
        FrequencyWeekday: formData.Repeating ? formData.FrequencyWeekday : null,
        FrequencyMonthType: formData.Repeating ? formData.FrequencyMonthType : null,
        FrequencyMonth: formData.Repeating ? formData.FrequencyMonth : null,
        FrequencyYears: formData.Repeating ? formData.FrequencyYears : null
      };
      const id = todo?.Id ?? todo?.id;
      if (id) {
        await apiService.updateTodo(id, payload);
      } else {
        await apiService.createTodo(payload);
      }
      if (typeof onSaved === 'function') await onSaved();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save to-do');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const id = todo?.Id ?? todo?.id;
    if (!id || viewMode) return;
    setLoading(true);
    setError('');
    try {
      await apiService.deleteTodo(id);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('todos-changed'));
      }
      if (typeof onSaved === 'function') await onSaved();
      setConfirmDelete(false);
      onClose();
    } catch (err) {
      setConfirmDelete(false);
      setError(err.message || 'Failed to delete to-do');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{ zIndex: 2000 }}
      PaperProps={{
        className: 'todo-form-modal-content',
        sx: {
          background: 'var(--color-bg-alt)',
          color: 'var(--color-text)'
        }
      }}
    >
      <DialogTitle sx={{ color: 'var(--color-text)', fontWeight: 600 }}>
        {viewMode ? 'View To-Do' : todo ? 'Edit To-Do' : 'Add To-Do'}
      </DialogTitle>
      <DialogContent sx={{ overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {assignedError && !assignedLoading && !lockLink && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAssignedError('')}>
            {assignedError}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <CustomTextInput
            label="Title *"
            value={formData.Title}
            onChange={(value) => setFormData({ ...formData, Title: value })}
            disabled={fieldsDisabled}
            error={Boolean(titleError)}
            helperText={titleError}
          />
          <CustomDatePicker
            label={formData.Repeating ? 'Due Date *' : 'Due Date'}
            value={formData.DueDate}
            onChange={(value) => {
              setDueDateError('');
              setFormData({ ...formData, DueDate: value });
            }}
            disabled={fieldsDisabled}
            error={Boolean(dueDateError)}
            helperText={dueDateError}
          />
          <CursorTooltip title="Yes creates a new to-do on a schedule. Choose how often, and how many days before the due date the next one should appear.">
            <Box>
              <YesNoRadio
                label="Repeating Task"
                value={Boolean(formData.Repeating)}
                onChange={handleRepeatingChange}
                disabled={fieldsDisabled}
              />
            </Box>
          </CursorTooltip>
          {formData.Repeating && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {nextAlreadyCreated && !viewMode && (
                <Alert severity="info">
                  The next occurrence has already been created. Frequency changes here apply to this to-do only.
                </Alert>
              )}
              <CustomDropdown
                label="Frequency *"
                value={formData.Frequency || ''}
                onChange={handleFrequencyChange}
                options={TODO_FREQUENCY_OPTIONS}
                placeholder="Select Frequency"
                fullWidth
                menuPortal
                disabled={fieldsDisabled}
                error={Boolean(frequencyError)}
                helperText={frequencyError}
              />
              {formData.Frequency ? (
                <FrequencySentenceDisplay
                  frequency={formData.Frequency}
                  config={frequencyConfig}
                  onChange={(newConfig) => {
                    const freq = formData.Frequency;
                    const years = newConfig.FrequencyYears !== undefined
                      ? newConfig.FrequencyYears
                      : formData.FrequencyYears;
                    if (freq) {
                      const maxDays = getMaxGenerateDaysBefore(freq, years);
                      const currentDays = newConfig.GenerateDaysBefore !== undefined
                        ? newConfig.GenerateDaysBefore
                        : formData.GenerateDaysBefore || 0;
                      if (currentDays > maxDays) {
                        newConfig.GenerateDaysBefore = maxDays;
                      }
                    }
                    setFrequencyError('');
                    setFormData((prev) => ({ ...prev, ...newConfig }));
                  }}
                  readOnly={Boolean(viewMode)}
                  showAdvancedOptions
                  showReminderDays={false}
                  menuPortal
                />
              ) : null}
              {nextRunDate && nextDueDate && (
                <Box
                  sx={{
                    p: '8px 12px',
                    backgroundColor: 'rgba(255, 193, 7, 0.1)',
                    borderLeft: '3px solid #ffc107',
                    borderRadius: '4px'
                  }}
                >
                  <Typography sx={{ color: 'var(--color-text)', fontSize: 14, fontStyle: 'italic' }}>
                    Next to-do will be created {formatGridDate(nextAlreadyCreated ? todo?.NextRun : nextRunDate) || 'on the next scheduler run'}
                    {` and will be due ${formatGridDate(nextDueDate)}`}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          <CustomDropdown
            label="Priority"
            value={formData.Priority}
            onChange={(value) => setFormData({ ...formData, Priority: value || 'Medium' })}
            options={TODO_PRIORITIES}
            placeholder="Select Priority"
            fullWidth
            menuPortal
            disabled={fieldsDisabled}
          />
          <CursorTooltip title="Optional tag. On the to-do list you can filter and sort by it.">
            <Box>
              <CustomTextInput
                label="Tag"
                value={formData.Label}
                onChange={(value) => setFormData({ ...formData, Label: value })}
                placeholder="Enter a tag"
                disabled={fieldsDisabled}
              />
            </Box>
          </CursorTooltip>
          <CursorTooltip title="Optional color shown on the list and calendar.">
            <Box className="todo-color-row">
              <span className="todo-color-row__label">Color</span>
              <Box className="todo-color-row__swatches">
                {TODO_COLOR_PALETTE.map((hex) => (
                  <button
                    key={hex}
                    type="button"
                    className={`todo-color-row__swatch${formData.Color === hex ? ' is-selected' : ''}`}
                    style={{ background: hex }}
                    onClick={() => !fieldsDisabled && setFormData({
                      ...formData,
                      Color: formData.Color === hex ? '' : hex
                    })}
                    disabled={fieldsDisabled}
                    aria-label={`Color ${hex}`}
                  />
                ))}
                <input
                  type="color"
                  className="todo-color-row__input"
                  value={formData.Color || '#3b82f6'}
                  onChange={(e) => setFormData({ ...formData, Color: normalizeTodoColor(e.target.value) })}
                  disabled={fieldsDisabled}
                  aria-label="Custom color"
                />
              </Box>
            </Box>
          </CursorTooltip>
          <CursorTooltip title={lockLink ? 'This to-do will be linked to the record you were viewing.' : 'Optional. Choose a form type, then pick an open record you are assigned to.'}>
            {lockLink ? (
              <Box sx={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>
                Linked to {formatLockedLinkedRecordLabel(formData.LinkedTableName, formData.LinkedRecordId)}
              </Box>
            ) : (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                width: '100%'
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <CustomDropdown
                  label="Form Type"
                  value={formData.LinkedTableName}
                  onChange={handleFormTypeChange}
                  options={formTypeOptions}
                  placeholder={assignedLoading ? 'Loading...' : 'Select Form Type'}
                  fullWidth
                  menuPortal
                  disabled={fieldsDisabled || assignedLoading}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <CustomDropdown
                  label="Record"
                  value={formData.LinkedRecordId}
                  onChange={(value) => setFormData({ ...formData, LinkedRecordId: value || '' })}
                  options={recordOptions}
                  placeholder={
                    assignedLoading
                      ? 'Loading...'
                      : formData.LinkedTableName
                        ? 'Select Record'
                        : 'Select Form Type first'
                  }
                  fullWidth
                  menuPortal
                  disabled={fieldsDisabled || assignedLoading || !formData.LinkedTableName}
                />
              </Box>
            </Box>
            )}
          </CursorTooltip>
          {canOpenLinked && !lockLink && (
            <Button
              onClick={() => onOpenLinkedRecord({
                tableName: linkedInfo.tableName,
                recordId: linkedInfo.recordId
              })}
              sx={{
                alignSelf: 'flex-start',
                textTransform: 'none',
                color: 'var(--color-accent)',
                fontWeight: 600
              }}
            >
              Open linked record
            </Button>
          )}
          <CustomTextArea
            label="Notes"
            value={formData.Notes}
            onChange={(value) => setFormData({ ...formData, Notes: value })}
            rows={4}
            disabled={fieldsDisabled}
          />
          <CursorTooltip title="Completed to-dos stay on your list until you hide them or delete them.">
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formData.Completed)}
                  onChange={(e) => setFormData({ ...formData, Completed: e.target.checked })}
                  disabled={fieldsDisabled}
                />
              }
              label="Completed"
              sx={{ color: 'var(--color-text)' }}
            />
          </CursorTooltip>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap'
        }}
      >
        <Box>
          {todo && !viewMode ? (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setConfirmDelete(true)}
              disabled={loading}
              sx={{ textTransform: 'none' }}
            >
              Delete
            </Button>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={loading}
            sx={{
              borderColor: 'var(--color-border)',
              color: 'var(--color-text)',
              textTransform: 'none'
            }}
          >
            {viewMode ? 'Close' : 'Cancel'}
          </Button>
          {!viewMode && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              sx={{
                background: 'linear-gradient(135deg, var(--color-accent), var(--color-accent2))',
                color: '#fff',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, var(--color-accent), var(--color-accent2))'
                }
              }}
            >
              {loading ? 'Saving...' : (todo ? 'Save To-Do' : 'Add To-Do')}
            </Button>
          )}
        </Box>
      </DialogActions>
      <Dialog
        open={confirmDelete}
        onClose={() => !loading && setConfirmDelete(false)}
        maxWidth="xs"
        sx={{ zIndex: 2100 }}
        PaperProps={{
          sx: {
            background: 'var(--color-bg-alt)',
            color: 'var(--color-text)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'var(--color-text)', fontWeight: 600 }}>
          Delete To-Do
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--color-text)' }}>
            Delete this to-do? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setConfirmDelete(false)}
            disabled={loading}
            sx={{
              borderColor: 'var(--color-border)',
              color: 'var(--color-text)',
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default TodoFormModal;
